import { BackButton, ISelectType, useFetch } from "@4uhub/lib4uhub";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import MainApi from "../../../../services/mainApi.service";
import ReportExecutionFilters, {
  IReportExecutionValidationErrors,
} from "./ReportExecutionFilters";
import { EReportCode } from "./reportConstants";
import {
  buildReportExecutionSearchParams,
  buildReportResultSearchParams,
  getMonthlyPeriodRange,
  isSupportedReportCode,
  isValidReportDateIntervalType,
  parseReportExecutionSearchParams,
  REPORT_EXECUTION_TRANSLATION_PATH,
  shouldShowAutomationCodeExceptForReport,
  shouldShowAutomationCodeForReport,
  shouldShowEsignRulesForReport,
  shouldShowEstablishmentsForReport,
  shouldShowTicketChannelsForReport,
  shouldShowTicketServicesForReport,
  shouldShowUsersForReport,
  shouldShowWorkspacesForReport,
  shouldRequireTicketServicesForReport,
  shouldRequireTicketChannelsForReport,
  shouldUseMonthPeriodForReport,
  resolveMonthlyPeriodRange,
  serializeLocalDateTime,
  type TReportDateIntervalType,
  type TReportMonthPeriodType,
} from "./reportExecutionUtils";
import { listAutomations } from "../../../../services/ticketautomation.service";
import EstablishmentsService from "../../../../services/establishments.service";
import { eSignRuleList } from "../../../../services/esignRule.service";
import { ticketChannelsByWorkspace } from "../../../../services/ticketChannelStep.service";
import { workspacesList } from "../../../../services/ticketWorkspace.service";
import UsersService from "../../../../services/users.service";

interface IReportExecutionLocationState {
  reportCode?: EReportCode | string | number;
  reportName?: string | null;
}

interface IReportExecutionPeriodFilter {
  CreatedStart: Date | null;
  CreatedEnd: Date | null;
}

interface IReportExecutionInitialState {
  periodFilter: IReportExecutionPeriodFilter;
  selectedDateIntervalType: TReportDateIntervalType;
  selectedMonthPeriodType: TReportMonthPeriodType;
  ticketServiceIds: string[];
  ticketChannelIds: string[];
  workspaceIds: string[];
  userIds: string[];
  automationCodes: string[];
  automationCodeExcepts: string[];
  establishmentIds: string[];
  esignRuleIds: string[];
}

const DEFAULT_DATE_INTERVAL_TYPE: TReportDateIntervalType = "today";
const DEFAULT_MONTH_PERIOD_TYPE: TReportMonthPeriodType = "this_month";

const parseQueryDate = (value?: string) => {
  if (!value) return null;

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const isValidDate = (date?: Date | null): date is Date =>
  date instanceof Date && !Number.isNaN(date.getTime());

const serializeValidDateTime = (date?: Date | null) =>
  isValidDate(date) ? date.toISOString() : undefined;

const getStartOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const getEndOfDay = (date: Date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  );

const getWeekRange = (date: Date) => {
  const start = new Date(date);
  start.setDate(date.getDate() - date.getDay());

  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  return {
    start: getStartOfDay(start),
    end: getEndOfDay(end),
  };
};

const getLast30DaysRange = (date: Date) => {
  const start = new Date(date);
  start.setDate(date.getDate() - 29);

  return {
    start: getStartOfDay(start),
    end: getEndOfDay(date),
  };
};

const getLast6MonthsRange = (date: Date) => ({
  start: getStartOfDay(new Date(date.getFullYear(), date.getMonth() - 5, 1)),
  end: getEndOfDay(date),
});

const resolveDateRangeFromInterval = (
  intervalType: TReportDateIntervalType,
) => {
  const now = new Date();

  switch (intervalType) {
    case "this_week":
      return getWeekRange(now);
    case "this_month":
      return getMonthlyPeriodRange(now);
    case "last_month":
      return resolveMonthlyPeriodRange({
        monthPeriodType: "last_month",
        referenceDate: now,
      });
    case "last_30_days":
      return getLast30DaysRange(now);
    case "last_6_months":
      return getLast6MonthsRange(now);
    case "select_period":
      return {
        start: null,
        end: null,
      };
    case "today":
    default:
      return {
        start: getStartOfDay(now),
        end: getEndOfDay(now),
      };
  }
};

const isSameDateTime = (left: Date | null, right: Date | null) =>
  isValidDate(left) && isValidDate(right)
    ? left.getTime() === right.getTime()
    : left === right;

const resolveMonthPeriodTypeFromDates = (
  createdStart: Date | null,
  createdEnd: Date | null,
): TReportMonthPeriodType => {
  if (!createdStart || !createdEnd) {
    return DEFAULT_MONTH_PERIOD_TYPE;
  }

  const now = new Date();
  const currentMonth = getMonthlyPeriodRange(now);

  if (
    isSameDateTime(createdStart, currentMonth.start) &&
    isSameDateTime(createdEnd, currentMonth.end)
  ) {
    return "this_month";
  }

  const previousMonth = getMonthlyPeriodRange(
    new Date(now.getFullYear(), now.getMonth() - 1, 1),
  );

  if (
    isSameDateTime(createdStart, previousMonth.start) &&
    isSameDateTime(createdEnd, previousMonth.end)
  ) {
    return "last_month";
  }

  return "select_month";
};

const resolveInitialDateIntervalType = ({
  queryDateIntervalType,
  queryStartDate,
  queryEndDate,
  shouldUseMonthPeriod,
}: {
  queryDateIntervalType?: TReportDateIntervalType;
  queryStartDate: Date | null;
  queryEndDate: Date | null;
  shouldUseMonthPeriod: boolean;
}): TReportDateIntervalType => {
  if (shouldUseMonthPeriod) {
    return DEFAULT_DATE_INTERVAL_TYPE;
  }

  if (queryDateIntervalType) {
    return queryDateIntervalType;
  }

  if (queryStartDate || queryEndDate) {
    return "select_period";
  }

  return DEFAULT_DATE_INTERVAL_TYPE;
};

const resolveInitialMonthPeriodType = ({
  queryMonthPeriodType,
  querySelectedMonthDate,
  queryStartDate,
  queryEndDate,
  shouldUseMonthPeriod,
}: {
  queryMonthPeriodType?: TReportMonthPeriodType;
  querySelectedMonthDate: Date | null;
  queryStartDate: Date | null;
  queryEndDate: Date | null;
  shouldUseMonthPeriod: boolean;
}): TReportMonthPeriodType => {
  if (!shouldUseMonthPeriod) {
    return DEFAULT_MONTH_PERIOD_TYPE;
  }

  if (queryMonthPeriodType) {
    return queryMonthPeriodType;
  }

  if (querySelectedMonthDate) {
    return "select_month";
  }

  if (queryStartDate || queryEndDate) {
    return resolveMonthPeriodTypeFromDates(queryStartDate, queryEndDate);
  }

  return DEFAULT_MONTH_PERIOD_TYPE;
};

const createFallbackOption = (value: string): ISelectType => ({
  id: value,
  name: value,
  code: value,
});

const createDateRangeOption = (
  id: TReportDateIntervalType,
  name: string,
): ISelectType => ({
  id,
  name,
  value: id,
});

const resolveSelectedById = (values: string[], options: ISelectType[]) =>
  values.map((value) => {
    const matchingOption = options.find((option) => option.id === value);

    return matchingOption
      ? {
          ...matchingOption,
          code: matchingOption.code ?? value,
        }
      : createFallbackOption(value);
  });

const resolveSelectedByCode = (values: string[], options: ISelectType[]) =>
  values.map(
    (value) =>
      options.find((option) => (option.code ?? option.id) === value) ??
      createFallbackOption(value),
  );

const TICKETS_ROUTE = process.env.REACT_APP_TICKETS;

const TICKET_SERVICES_ROUTE = `${TICKETS_ROUTE}/api/v1/TicketServices/`;

const ticketServicesService = new MainApi<ISelectType>(TICKET_SERVICES_ROUTE);

const establishmentListService = new EstablishmentsService();
const usersService = new UsersService();

const hasValidationErrors = (errors: IReportExecutionValidationErrors) =>
  Object.values(errors).some(Boolean);

const areSameStringArrays = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const isInvalidDateRange = (
  createdStart: Date | null,
  createdEnd: Date | null,
) =>
  Boolean(
    isValidDate(createdStart) &&
      isValidDate(createdEnd) &&
      createdStart > createdEnd,
  );

const ReportExecutionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentQueryString = useMemo(
    () => searchParams.toString(),
    [searchParams],
  );
  const lastAppliedQueryKeyRef = useRef<string | null>(null);

  const routeState = state as IReportExecutionLocationState | null;
  const initialSearchParams = useMemo(
    () => parseReportExecutionSearchParams(searchParams),
    [searchParams],
  );

  const reportCode = useMemo(() => {
    const queryReportCode = Number(searchParams.get("reportCode"));

    if (isSupportedReportCode(queryReportCode)) {
      return queryReportCode;
    }

    const stateReportCode = Number(routeState?.reportCode);

    return isSupportedReportCode(stateReportCode) ? stateReportCode : undefined;
  }, [routeState?.reportCode, searchParams]);

  const reportName = searchParams.get("reportName") ?? routeState?.reportName;
  const dateRangeOptions = useMemo<ISelectType[]>(
    () => [
      createDateRangeOption(
        "today",
        t("Lib4uhub.components.date_range.today", "Hoje"),
      ),
      createDateRangeOption(
        "this_week",
        t("Lib4uhub.components.date_range.this_week", "Esta semana"),
      ),
      createDateRangeOption(
        "this_month",
        t("Lib4uhub.components.date_range.this_month", "Este mês"),
      ),
      createDateRangeOption(
        "last_month",
        t("Lib4uhub.components.date_range.last_month", "Mês anterior"),
      ),
      createDateRangeOption(
        "last_30_days",
        t("Lib4uhub.components.date_range.last_30_days", "Últimos 30 dias"),
      ),
      createDateRangeOption(
        "last_6_months",
        t("Lib4uhub.components.date_range.last_6_months", "Últimos 6 meses"),
      ),
      createDateRangeOption(
        "select_period",
        t("Lib4uhub.components.date_range.select_period", "Selecionar período"),
      ),
    ],
    [t],
  );
  const [periodFilter, setPeriodFilter] =
    useState<IReportExecutionPeriodFilter>({
      CreatedStart: null,
      CreatedEnd: null,
    });
  const [selectedDateIntervalType, setSelectedDateIntervalType] =
    useState<TReportDateIntervalType>(DEFAULT_DATE_INTERVAL_TYPE);
  const [selectedMonthPeriodType, setSelectedMonthPeriodType] =
    useState<TReportMonthPeriodType>(DEFAULT_MONTH_PERIOD_TYPE);
  const [ticketServiceIds, setTicketServiceIds] = useState<string[]>([]);
  const [ticketChannelIds, setTicketChannelIds] = useState<string[]>([]);
  const [workspaceIds, setWorkspaceIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [automationCodes, setAutomationCodes] = useState<string[]>([]);
  const [automationCodeExcepts, setAutomationCodeExcepts] = useState<string[]>(
    [],
  );
  const [establishmentIds, setEstablishmentIds] = useState<string[]>([]);
  const [esignRuleIds, setEsignRuleIds] = useState<string[]>([]);
  const [ticketServiceOptions, setTicketServiceOptions] = useState<
    ISelectType[]
  >([]);
  const [ticketChannelOptions, setTicketChannelOptions] = useState<
    ISelectType[]
  >([]);
  const [workspaceOptions, setWorkspaceOptions] = useState<ISelectType[]>([]);
  const [userOptions, setUserOptions] = useState<ISelectType[]>([]);
  const [automationOptions, setAutomationOptions] = useState<ISelectType[]>([]);
  const [establishmentOptions, setEstablishmentOptions] = useState<
    ISelectType[]
  >([]);
  const [esignRuleOptions, setEsignRuleOptions] = useState<ISelectType[]>([]);
  const [isInitializingFilters, setIsInitializingFilters] = useState(true);
  const [hasAttemptedGenerate, setHasAttemptedGenerate] = useState(false);

  const { sendRequest: listTicketServices } = useFetch(
    ticketServicesService.list,
  );
  const { sendRequest: listWorkspaces } = useFetch(workspacesList);
  const { sendRequest: listAutomationOptions } = useFetch(listAutomations);
  const { sendRequest: listEstablishmentOptions } = useFetch(
    establishmentListService.masterList,
  );
  const { sendRequest: listUserOptions } = useFetch(
    usersService.listTicketUsers,
  );
  const { sendRequest: listEsignRuleOptions } = useFetch(eSignRuleList);

  const shouldShowTicketServices = useMemo(
    () => shouldShowTicketServicesForReport(reportCode),
    [reportCode],
  );

  const shouldRequireTicketServices = useMemo(
    () => shouldRequireTicketServicesForReport(reportCode),
    [reportCode],
  );

  const shouldShowTicketChannels = useMemo(
    () => shouldShowTicketChannelsForReport(reportCode),
    [reportCode],
  );

  const shouldRequireTicketChannels = useMemo(
    () => shouldRequireTicketChannelsForReport(reportCode),
    [reportCode],
  );

  const shouldShowWorkspaces = useMemo(
    () => shouldShowWorkspacesForReport(reportCode),
    [reportCode],
  );

  const shouldShowUsers = useMemo(
    () => shouldShowUsersForReport(reportCode),
    [reportCode],
  );

  const shouldUseMonthPeriod = useMemo(
    () => shouldUseMonthPeriodForReport(reportCode),
    [reportCode],
  );

  const shouldShowAutomationCode = useMemo(
    () => shouldShowAutomationCodeForReport(reportCode),
    [reportCode],
  );

  const shouldShowAutomationCodeExcept = useMemo(
    () => shouldShowAutomationCodeExceptForReport(reportCode),
    [reportCode],
  );

  const shouldShowEstablishments = useMemo(
    () => shouldShowEstablishmentsForReport(reportCode),
    [reportCode],
  );

  const shouldShowEsignRules = useMemo(
    () => shouldShowEsignRulesForReport(reportCode),
    [reportCode],
  );
  const queryInitializationKey = useMemo(
    () => `${reportCode ?? ""}|${currentQueryString}`,
    [currentQueryString, reportCode],
  );

  const initialFilterState = useMemo<IReportExecutionInitialState>(() => {
    const queryStartDate = parseQueryDate(initialSearchParams.startDate);
    const queryEndDate = parseQueryDate(initialSearchParams.endDate);
    const querySelectedMonthDate = parseQueryDate(
      initialSearchParams.selectedMonthDate,
    );
    const resolvedDateIntervalType = resolveInitialDateIntervalType({
      queryDateIntervalType: initialSearchParams.dateIntervalType,
      queryStartDate,
      queryEndDate,
      shouldUseMonthPeriod,
    });
    const resolvedMonthPeriodType = resolveInitialMonthPeriodType({
      queryMonthPeriodType: initialSearchParams.monthPeriodType,
      querySelectedMonthDate,
      queryStartDate,
      queryEndDate,
      shouldUseMonthPeriod,
    });

    let resolvedPeriodFilter: IReportExecutionPeriodFilter;

    if (shouldUseMonthPeriod) {
      const resolvedRange = resolveMonthlyPeriodRange({
        monthPeriodType: resolvedMonthPeriodType,
        selectedMonthDate: querySelectedMonthDate,
        startDate: queryStartDate,
        endDate: queryEndDate,
      });

      resolvedPeriodFilter = {
        CreatedStart: resolvedRange.start,
        CreatedEnd: resolvedRange.end,
      };
    } else {
      const resolvedRange = resolveDateRangeFromInterval(
        resolvedDateIntervalType,
      );
      resolvedPeriodFilter = {
        CreatedStart:
          resolvedDateIntervalType === "select_period"
            ? queryStartDate
            : resolvedRange.start,
        CreatedEnd:
          resolvedDateIntervalType === "select_period"
            ? queryEndDate
            : resolvedRange.end,
      };
    }

    return {
      periodFilter: resolvedPeriodFilter,
      selectedDateIntervalType: resolvedDateIntervalType,
      selectedMonthPeriodType: resolvedMonthPeriodType,
      ticketServiceIds: shouldShowTicketServices
        ? (initialSearchParams.ticketServiceIds ?? [])
        : [],
      ticketChannelIds: shouldShowTicketChannels
        ? (initialSearchParams.ticketChannelIds ?? [])
        : [],
      workspaceIds: shouldShowWorkspaces
        ? (initialSearchParams.ticketWorkspaceIds?.length
          ? initialSearchParams.ticketWorkspaceIds
          : (initialSearchParams.workspaceId ?? []))
        : [],
      userIds: shouldShowUsers ? (initialSearchParams.userIds ?? []) : [],
      automationCodes: shouldShowAutomationCode
        ? (initialSearchParams.automationCode ?? [])
        : [],
      automationCodeExcepts: shouldShowAutomationCodeExcept
        ? (initialSearchParams.automationCodeExcept ?? [])
        : [],
      establishmentIds: shouldShowEstablishments
        ? (initialSearchParams.establishmentId ?? [])
        : [],
      esignRuleIds: shouldShowEsignRules
        ? (initialSearchParams.esignRuleId ?? [])
        : [],
    };
  }, [
    initialSearchParams.automationCode,
    initialSearchParams.automationCodeExcept,
    initialSearchParams.dateIntervalType,
    initialSearchParams.endDate,
    initialSearchParams.esignRuleId,
    initialSearchParams.establishmentId,
    initialSearchParams.monthPeriodType,
    initialSearchParams.selectedMonthDate,
    initialSearchParams.startDate,
    initialSearchParams.ticketChannelIds,
    initialSearchParams.ticketServiceIds,
    initialSearchParams.userIds,
    initialSearchParams.workspaceId,
    shouldShowAutomationCode,
    shouldShowAutomationCodeExcept,
    shouldShowEsignRules,
    shouldShowEstablishments,
    shouldShowTicketChannels,
    shouldShowTicketServices,
    shouldShowUsers,
    shouldShowWorkspaces,
    shouldUseMonthPeriod,
  ]);

  const selectedDateInterval = useMemo(
    () =>
      dateRangeOptions.find(
        (option) => option.id === selectedDateIntervalType,
      ) ??
      dateRangeOptions.find(
        (option) => option.id === DEFAULT_DATE_INTERVAL_TYPE,
      ) ??
      createDateRangeOption(
        DEFAULT_DATE_INTERVAL_TYPE,
        t("Lib4uhub.components.date_range.today", "Hoje"),
      ),
    [dateRangeOptions, selectedDateIntervalType, t],
  );

  const selectedTicketServices = useMemo(
    () => resolveSelectedById(ticketServiceIds, ticketServiceOptions),
    [ticketServiceIds, ticketServiceOptions],
  );

  const selectedTicketChannels = useMemo(
    () => resolveSelectedById(ticketChannelIds, ticketChannelOptions),
    [ticketChannelIds, ticketChannelOptions],
  );

  const selectedWorkspaces = useMemo(
    () => resolveSelectedById(workspaceIds, workspaceOptions),
    [workspaceIds, workspaceOptions],
  );

  const selectedUsers = useMemo(
    () => resolveSelectedById(userIds, userOptions),
    [userIds, userOptions],
  );

  const selectedAutomationCode = useMemo(
    () => resolveSelectedByCode(automationCodes, automationOptions),
    [automationCodes, automationOptions],
  );

  const selectedAutomationCodeExcept = useMemo(
    () => resolveSelectedByCode(automationCodeExcepts, automationOptions),
    [automationCodeExcepts, automationOptions],
  );

  const selectedEstablishments = useMemo(
    () => resolveSelectedById(establishmentIds, establishmentOptions),
    [establishmentIds, establishmentOptions],
  );

  const selectedEsignRules = useMemo(
    () => resolveSelectedById(esignRuleIds, esignRuleOptions),
    [esignRuleIds, esignRuleOptions],
  );

  const monthlyPeriodRange = useMemo(() => {
    if (!shouldUseMonthPeriod) {
      return null;
    }

    return resolveMonthlyPeriodRange({
      monthPeriodType: selectedMonthPeriodType,
      selectedMonthDate: periodFilter.CreatedStart,
      startDate: periodFilter.CreatedStart,
      endDate: periodFilter.CreatedEnd,
    });
  }, [
    periodFilter.CreatedEnd,
    periodFilter.CreatedStart,
    selectedMonthPeriodType,
    shouldUseMonthPeriod,
  ]);

  const pageTitle = useMemo(
    () =>
      reportName ?? (t(REPORT_EXECUTION_TRANSLATION_PATH + "title") as string),
    [reportName, t],
  );

  const validationErrors = useMemo<IReportExecutionValidationErrors>(() => {
    const errors: IReportExecutionValidationErrors = {};

    if (shouldUseMonthPeriod) {
      if (
        !isValidDate(periodFilter.CreatedStart) ||
        !isValidDate(periodFilter.CreatedEnd)
      ) {
        errors.created = t(
          REPORT_EXECUTION_TRANSLATION_PATH + "validation.createdRequired",
          "Selecione um mes",
        ) as string;
      }
    } else if (!isValidDate(periodFilter.CreatedStart)) {
      errors.createdStart = t(
        REPORT_EXECUTION_TRANSLATION_PATH + "validation.startDateRequired",
        "A data inicial é obrigatória",
      ) as string;
    }

    if (!shouldUseMonthPeriod && !isValidDate(periodFilter.CreatedEnd)) {
      errors.createdEnd = t(
        REPORT_EXECUTION_TRANSLATION_PATH + "validation.endDateRequired",
        "A data final é obrigatória",
      ) as string;
    }

    if (shouldRequireTicketServices && selectedTicketServices.length === 0) {
      errors.ticketServices = t(
        REPORT_EXECUTION_TRANSLATION_PATH + "validation.ticketServicesRequired",
        "Selecione ao menos um serviço de ticket",
      ) as string;
    }

    if (shouldRequireTicketChannels && selectedTicketChannels.length === 0) {
      errors.ticketChannels = t(
        REPORT_EXECUTION_TRANSLATION_PATH + "validation.channelsRequired",
        "Selecione ao menos um quadro",
      ) as string;
    }

    if (shouldShowWorkspaces && selectedWorkspaces.length === 0) {
      errors.workspaces = t(
        REPORT_EXECUTION_TRANSLATION_PATH + "validation.workspacesRequired",
        "Selecione ao menos uma área de trabalho",
      ) as string;
    }

    return errors;
  }, [
    periodFilter.CreatedEnd,
    periodFilter.CreatedStart,
    selectedTicketChannels.length,
    selectedTicketServices.length,
    selectedWorkspaces.length,
    shouldShowWorkspaces,
    shouldRequireTicketServices,
    shouldRequireTicketChannels,
    shouldUseMonthPeriod,
    t,
  ]);

  const dateRangeValidationError = useMemo(() => {
    if (
      shouldUseMonthPeriod ||
      !isInvalidDateRange(periodFilter.CreatedStart, periodFilter.CreatedEnd)
    ) {
      return undefined;
    }

    return t(
      REPORT_EXECUTION_TRANSLATION_PATH + "validation.invalidDateRange",
      "A data inicial não pode ser maior que a data final",
    ) as string;
  }, [
    periodFilter.CreatedEnd,
    periodFilter.CreatedStart,
    shouldUseMonthPeriod,
    t,
  ]);

  const mergedValidationErrors = useMemo<IReportExecutionValidationErrors>(
    () => ({
      ...validationErrors,
      ...(dateRangeValidationError
        ? { createdRange: dateRangeValidationError }
        : {}),
    }),
    [dateRangeValidationError, validationErrors],
  );

  const displayValidationErrors = useMemo<IReportExecutionValidationErrors>(
    () => (hasAttemptedGenerate ? mergedValidationErrors : {}),
    [hasAttemptedGenerate, mergedValidationErrors],
  );

  const isFormValid = useMemo(
    () => !hasValidationErrors(mergedValidationErrors),
    [mergedValidationErrors],
  );

  const loadTicketServicesOptions = useCallback(async () => {
    const { data, success } = await listTicketServices(null);

    return success && data ? data : [];
  }, [listTicketServices]);

  const loadTicketChannelOptions = useCallback(async (ids: string[]) => {
    if (!ids.length) {
      return [];
    }

    const channelResponses = await Promise.all(
      ids.map((workspaceId) => ticketChannelsByWorkspace(workspaceId)),
    );

    const mergedChannels = channelResponses.flatMap(
      (response) => response.data ?? [],
    );

    return mergedChannels.filter(
      (channel, index, array) =>
        index === array.findIndex((item) => item.id === channel.id),
    );
  }, []);

  const loadWorkspaceOptions = useCallback(async () => {
    const { data, success } = await listWorkspaces(null);

    return success && data ? data : [];
  }, [listWorkspaces]);

  const loadAutomationOptions = useCallback(async () => {
    const { data, success } = await listAutomationOptions(null);

    return success && data ? data : [];
  }, [listAutomationOptions]);

  const loadUserOptions = useCallback(async () => {
    const { data, success } = await listUserOptions(null);

    return success && data ? data : [];
  }, [listUserOptions]);

  const loadEstablishmentOptions = useCallback(async () => {
    const { data, success } = await listEstablishmentOptions(null);

    return success && data
      ? data.map((item) => ({
          id: item.id,
          name: item.name,
          code: item.code ?? undefined,
        }))
      : [];
  }, [listEstablishmentOptions]);

  const loadEsignRuleOptions = useCallback(async () => {
    const { data, success } = await listEsignRuleOptions(null);

    return success && data ? data : [];
  }, [listEsignRuleOptions]);

  const changeTicketServicesHandler = useCallback(
    (value: ISelectType[] | null) => {
      const nextIds = value?.map((item) => item.id) ?? [];

      setTicketServiceIds((prev) =>
        areSameStringArrays(prev, nextIds) ? prev : nextIds,
      );
    },
    [],
  );

  const changeTicketChannelsHandler = useCallback(
    (value: ISelectType[] | null) => {
      const nextIds = value?.map((item) => item.id) ?? [];

      setTicketChannelIds((prev) =>
        areSameStringArrays(prev, nextIds) ? prev : nextIds,
      );
    },
    [],
  );

  const changeWorkspacesHandler = useCallback((value: ISelectType[] | null) => {
    const nextIds = value?.map((item) => item.id) ?? [];

    setWorkspaceIds((prev) =>
      areSameStringArrays(prev, nextIds) ? prev : nextIds,
    );
    setTicketChannelIds((prev) => (prev.length ? [] : prev));
  }, []);

  const changeAutomationCodeHandler = useCallback(
    (value: ISelectType[] | null) => {
      const nextCodes =
        value?.map((item) => item.code ?? item.id).filter(Boolean) ?? [];

      setAutomationCodes((prev) =>
        areSameStringArrays(prev, nextCodes) ? prev : nextCodes,
      );
    },
    [],
  );

  const changeUsersHandler = useCallback((value: ISelectType[] | null) => {
    const nextIds = value?.map((item) => item.id).filter(Boolean) ?? [];

    setUserIds((prev) => (areSameStringArrays(prev, nextIds) ? prev : nextIds));
  }, []);

  const changeAutomationCodeExceptHandler = useCallback(
    (value: ISelectType[] | null) => {
      const nextCodes =
        value?.map((item) => item.code ?? item.id).filter(Boolean) ?? [];

      setAutomationCodeExcepts((prev) =>
        areSameStringArrays(prev, nextCodes) ? prev : nextCodes,
      );
    },
    [],
  );

  const changeEstablishmentsHandler = useCallback(
    (value: ISelectType[] | null) => {
      const nextIds = value?.map((item) => item.id).filter(Boolean) ?? [];

      setEstablishmentIds((prev) =>
        areSameStringArrays(prev, nextIds) ? prev : nextIds,
      );
    },
    [],
  );

  const changeEsignRulesHandler = useCallback((value: ISelectType[] | null) => {
    const nextIds = value?.map((item) => item.id).filter(Boolean) ?? [];

    setEsignRuleIds((prev) =>
      areSameStringArrays(prev, nextIds) ? prev : nextIds,
    );
  }, []);

  const changeCreatedStartHandler = useCallback((date: Date | null) => {
    setPeriodFilter((prev) => ({
      ...prev,
      CreatedStart: isSameDateTime(prev.CreatedStart, date)
        ? prev.CreatedStart
        : date,
    }));
  }, []);

  const changeCreatedEndHandler = useCallback((date: Date | null) => {
    setPeriodFilter((prev) => ({
      ...prev,
      CreatedEnd: isSameDateTime(prev.CreatedEnd, date)
        ? prev.CreatedEnd
        : date,
    }));
  }, []);

  const changeDateIntervalHandler = useCallback((value: ISelectType | null) => {
    const nextValue = value?.id;

    if (!isValidReportDateIntervalType(nextValue)) {
      const defaultRange = resolveDateRangeFromInterval(
        DEFAULT_DATE_INTERVAL_TYPE,
      );

      setSelectedDateIntervalType((prev) =>
        prev === DEFAULT_DATE_INTERVAL_TYPE ? prev : DEFAULT_DATE_INTERVAL_TYPE,
      );

      setPeriodFilter((prev) =>
        isSameDateTime(prev.CreatedStart, defaultRange.start) &&
        isSameDateTime(prev.CreatedEnd, defaultRange.end)
          ? prev
          : {
              CreatedStart: defaultRange.start,
              CreatedEnd: defaultRange.end,
            },
      );

      return;
    }

    setSelectedDateIntervalType((prev) =>
      prev === nextValue ? prev : nextValue,
    );

    if (nextValue === "select_period") {
      return;
    }

    const nextRange = resolveDateRangeFromInterval(nextValue);

    setPeriodFilter((prev) =>
      isSameDateTime(prev.CreatedStart, nextRange.start) &&
      isSameDateTime(prev.CreatedEnd, nextRange.end)
        ? prev
        : {
            CreatedStart: nextRange.start,
            CreatedEnd: nextRange.end,
          },
    );
  }, []);

  const changeMonthPeriodTypeHandler = useCallback(
    (value: TReportMonthPeriodType) => {
      setSelectedMonthPeriodType((prev) => (prev === value ? prev : value));

      const range = resolveMonthlyPeriodRange({ monthPeriodType: value });

      setPeriodFilter((prev) =>
        isSameDateTime(prev.CreatedStart, range.start) &&
        isSameDateTime(prev.CreatedEnd, range.end)
          ? prev
          : {
              CreatedStart: range.start,
              CreatedEnd: range.end,
            },
      );
    },
    [],
  );

  const changeSelectedMonthHandler = useCallback((date: Date | null) => {
    setSelectedMonthPeriodType((prev) =>
      prev === "select_month" ? prev : "select_month",
    );

    if (!date) {
      setPeriodFilter((prev) =>
        prev.CreatedStart === null && prev.CreatedEnd === null
          ? prev
          : {
              CreatedStart: null,
              CreatedEnd: null,
            },
      );

      return;
    }

    const range = resolveMonthlyPeriodRange({
      monthPeriodType: "select_month",
      selectedMonthDate: date,
    });

    setPeriodFilter((prev) =>
      isSameDateTime(prev.CreatedStart, range.start) &&
      isSameDateTime(prev.CreatedEnd, range.end)
        ? prev
        : {
            CreatedStart: range.start,
            CreatedEnd: range.end,
          },
    );
  }, []);

  const handleGenerate = useCallback(() => {
    setHasAttemptedGenerate(true);

    if (
      isInitializingFilters ||
      !reportCode ||
      !isFormValid ||
      isInvalidDateRange(periodFilter.CreatedStart, periodFilter.CreatedEnd)
    ) {
      return;
    }

    const resultSearchParams = buildReportResultSearchParams({
      reportCode,
      reportName: reportName ?? undefined,
      dateIntervalType: !shouldUseMonthPeriod
        ? selectedDateIntervalType
        : undefined,
      monthPeriodType: shouldUseMonthPeriod
        ? selectedMonthPeriodType
        : undefined,
      selectedMonthDate:
        shouldUseMonthPeriod && selectedMonthPeriodType === "select_month"
          ? serializeLocalDateTime(monthlyPeriodRange?.start)
          : undefined,
      startDate: shouldUseMonthPeriod
        ? serializeLocalDateTime(monthlyPeriodRange?.start)
        : serializeValidDateTime(periodFilter.CreatedStart),
      endDate: shouldUseMonthPeriod
        ? serializeLocalDateTime(monthlyPeriodRange?.end)
        : serializeValidDateTime(periodFilter.CreatedEnd),
      ticketServiceIds: shouldShowTicketServices ? ticketServiceIds : undefined,
      ticketChannelIds: shouldShowTicketChannels ? ticketChannelIds : undefined,
      ticketWorkspaceIds: shouldShowWorkspaces ? workspaceIds : undefined,
      userIds: shouldShowUsers ? userIds : undefined,
      ticketServiceNames: selectedTicketServices
        .map((item) => item.name)
        .filter(Boolean),
      ticketChannelNames: selectedTicketChannels
        .map((item) => item.name)
        .filter(Boolean),
      userNames: shouldShowUsers
        ? selectedUsers.map((item) => item.name).filter(Boolean)
        : undefined,
      automationCode: shouldShowAutomationCode ? automationCodes : undefined,
      automationCodeExcept: shouldShowAutomationCodeExcept
        ? automationCodeExcepts
        : undefined,
      establishmentId: shouldShowEstablishments ? establishmentIds : undefined,
      esignRuleId: shouldShowEsignRules ? esignRuleIds : undefined,
      automationCodeNames: selectedAutomationCode
        .map((item) => item.name)
        .filter(Boolean),
      automationCodeExceptNames: selectedAutomationCodeExcept
        .map((item) => item.name)
        .filter(Boolean),
      establishmentNames: selectedEstablishments
        .map((item) => item.name)
        .filter(Boolean),
      esignRuleNames: selectedEsignRules
        .map((item) => item.name)
        .filter(Boolean),
      workspaceId: shouldShowWorkspaces ? workspaceIds : undefined,
      workspaceNames: selectedWorkspaces
        .map((item) => item.name)
        .filter(Boolean),
    });

    navigate(`result?${resultSearchParams.toString()}`);
  }, [
    isInitializingFilters,
    isFormValid,
    navigate,
    periodFilter.CreatedEnd,
    periodFilter.CreatedStart,
    reportCode,
    reportName,
    selectedDateIntervalType,
    selectedMonthPeriodType,
    monthlyPeriodRange,
    shouldShowAutomationCode,
    shouldShowAutomationCodeExcept,
    shouldShowEsignRules,
    shouldShowEstablishments,
    shouldShowTicketChannels,
    shouldShowTicketServices,
    shouldShowWorkspaces,
    shouldUseMonthPeriod,
    automationCodes,
    automationCodeExcepts,
    esignRuleIds,
    establishmentIds,
    selectedAutomationCode,
    selectedAutomationCodeExcept,
    selectedEsignRules,
    selectedEstablishments,
    selectedTicketChannels,
    selectedTicketServices,
    selectedUsers,
    shouldShowUsers,
    ticketChannelIds,
    ticketServiceIds,
    userIds,
    selectedWorkspaces,
    workspaceIds,
  ]);

  useEffect(() => {
    let isActive = true;

    if (queryInitializationKey === lastAppliedQueryKeyRef.current) {
      return;
    }

    const initializeFilters = async () => {
      setIsInitializingFilters(true);
      setHasAttemptedGenerate(false);

      setPeriodFilter(initialFilterState.periodFilter);
      setSelectedDateIntervalType(initialFilterState.selectedDateIntervalType);
      setSelectedMonthPeriodType(initialFilterState.selectedMonthPeriodType);
      setTicketServiceIds(initialFilterState.ticketServiceIds);
      setTicketChannelIds(initialFilterState.ticketChannelIds);
      setWorkspaceIds(initialFilterState.workspaceIds);
      setUserIds(initialFilterState.userIds);
      setAutomationCodes(initialFilterState.automationCodes);
      setAutomationCodeExcepts(initialFilterState.automationCodeExcepts);
      setEstablishmentIds(initialFilterState.establishmentIds);
      setEsignRuleIds(initialFilterState.esignRuleIds);

      const [
        nextTicketServiceOptions,
        nextWorkspaceOptions,
        nextTicketChannelOptions,
        nextUserOptions,
        nextAutomationOptions,
        nextEstablishmentOptions,
        nextEsignRuleOptions,
      ] = await Promise.all([
        shouldShowTicketServices
          ? loadTicketServicesOptions()
          : Promise.resolve([]),
        shouldShowWorkspaces ? loadWorkspaceOptions() : Promise.resolve([]),
        shouldShowTicketChannels
          ? loadTicketChannelOptions(initialFilterState.workspaceIds)
          : Promise.resolve([]),
        shouldShowUsers ? loadUserOptions() : Promise.resolve([]),
        shouldShowAutomationCode || shouldShowAutomationCodeExcept
          ? loadAutomationOptions()
          : Promise.resolve([]),
        shouldShowEstablishments
          ? loadEstablishmentOptions()
          : Promise.resolve([]),
        shouldShowEsignRules ? loadEsignRuleOptions() : Promise.resolve([]),
      ]);

      if (!isActive) {
        return;
      }

      setTicketServiceOptions(nextTicketServiceOptions);
      setWorkspaceOptions(nextWorkspaceOptions);
      setTicketChannelOptions(nextTicketChannelOptions);
      setUserOptions(nextUserOptions);
      setAutomationOptions(nextAutomationOptions);
      setEstablishmentOptions(nextEstablishmentOptions);
      setEsignRuleOptions(nextEsignRuleOptions);
      lastAppliedQueryKeyRef.current = queryInitializationKey;
      setIsInitializingFilters(false);
    };

    initializeFilters();

    return () => {
      isActive = false;
    };
  }, [
    initialFilterState,
    loadAutomationOptions,
    loadEsignRuleOptions,
    loadEstablishmentOptions,
    loadTicketChannelOptions,
    loadTicketServicesOptions,
    loadUserOptions,
    loadWorkspaceOptions,
    shouldShowAutomationCode,
    shouldShowAutomationCodeExcept,
    shouldShowEsignRules,
    shouldShowEstablishments,
    shouldShowTicketChannels,
    shouldShowTicketServices,
    shouldShowUsers,
    shouldShowWorkspaces,
    queryInitializationKey,
  ]);

  useEffect(() => {
    let isActive = true;

    if (isInitializingFilters || !shouldShowTicketChannels) {
      return;
    }

    const syncTicketChannels = async () => {
      const nextTicketChannelOptions =
        await loadTicketChannelOptions(workspaceIds);

      if (!isActive) {
        return;
      }

      setTicketChannelOptions(nextTicketChannelOptions);
    };

    syncTicketChannels();

    return () => {
      isActive = false;
    };
  }, [
    isInitializingFilters,
    loadTicketChannelOptions,
    shouldShowTicketChannels,
    workspaceIds,
  ]);

  useEffect(() => {
    if (isInitializingFilters) {
      return;
    }

    const nextSearchParams = buildReportExecutionSearchParams({
      reportCode,
      reportName: reportName ?? undefined,
      dateIntervalType: !shouldUseMonthPeriod
        ? selectedDateIntervalType
        : undefined,
      monthPeriodType: shouldUseMonthPeriod
        ? selectedMonthPeriodType
        : undefined,
      selectedMonthDate:
        shouldUseMonthPeriod && selectedMonthPeriodType === "select_month"
          ? serializeLocalDateTime(monthlyPeriodRange?.start)
          : undefined,
      startDate: shouldUseMonthPeriod
        ? serializeLocalDateTime(monthlyPeriodRange?.start)
        : serializeValidDateTime(periodFilter.CreatedStart),
      endDate: shouldUseMonthPeriod
        ? serializeLocalDateTime(monthlyPeriodRange?.end)
        : serializeValidDateTime(periodFilter.CreatedEnd),
      ticketServiceIds: shouldShowTicketServices ? ticketServiceIds : undefined,
      ticketChannelIds: shouldShowTicketChannels ? ticketChannelIds : undefined,
      workspaceId: shouldShowWorkspaces ? workspaceIds : undefined,
      ticketWorkspaceIds: shouldShowWorkspaces ? workspaceIds : undefined,
      userIds: shouldShowUsers ? userIds : undefined,
      automationCode: shouldShowAutomationCode ? automationCodes : undefined,
      automationCodeExcept: shouldShowAutomationCodeExcept
        ? automationCodeExcepts
        : undefined,
      establishmentId: shouldShowEstablishments ? establishmentIds : undefined,
      esignRuleId: shouldShowEsignRules ? esignRuleIds : undefined,
    });
    const nextQueryString = nextSearchParams.toString();

    if (nextQueryString !== currentQueryString) {
      lastAppliedQueryKeyRef.current = `${reportCode ?? ""}|${nextQueryString}`;
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    automationCodes,
    automationCodeExcepts,
    esignRuleIds,
    establishmentIds,
    isInitializingFilters,
    periodFilter.CreatedEnd,
    periodFilter.CreatedStart,
    reportCode,
    currentQueryString,
    reportName,
    selectedDateIntervalType,
    selectedMonthPeriodType,
    monthlyPeriodRange,
    setSearchParams,
    shouldShowAutomationCode,
    shouldShowAutomationCodeExcept,
    shouldShowEsignRules,
    shouldShowEstablishments,
    shouldShowTicketChannels,
    shouldShowTicketServices,
    shouldShowUsers,
    shouldShowWorkspaces,
    shouldUseMonthPeriod,
    ticketChannelIds,
    ticketServiceIds,
    userIds,
    workspaceIds,
  ]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="self-start">
        <BackButton
          onClick={() => navigate("../..", { relative: "path" })}
        />
      </div>

      {isInitializingFilters ? (
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: 3,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              {t(REPORT_EXECUTION_TRANSLATION_PATH + "loading", "Carregando")}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <ReportExecutionFilters
          title={pageTitle}
          reportCode={reportCode}
          createdStart={periodFilter.CreatedStart}
          createdEnd={periodFilter.CreatedEnd}
          onSelectedMonthChange={changeSelectedMonthHandler}
          selectedDateInterval={selectedDateInterval}
          selectedMonthPeriodType={selectedMonthPeriodType}
          selectedTicketServices={selectedTicketServices}
          selectedTicketChannels={selectedTicketChannels}
          selectedWorkspaces={selectedWorkspaces}
          selectedUsers={selectedUsers}
          selectedAutomationCode={selectedAutomationCode}
          selectedAutomationCodeExcept={selectedAutomationCodeExcept}
          selectedEstablishments={selectedEstablishments}
          selectedEsignRules={selectedEsignRules}
          ticketServiceOptions={ticketServiceOptions}
          ticketChannelOptions={ticketChannelOptions}
          workspaceOptions={workspaceOptions}
          userOptions={userOptions}
          automationOptions={automationOptions}
          establishmentOptions={establishmentOptions}
          esignRuleOptions={esignRuleOptions}
          shouldShowTicketServices={shouldShowTicketServices}
          shouldRequireTicketServices={shouldRequireTicketServices}
          shouldShowTicketChannels={shouldShowTicketChannels}
          shouldRequireTicketChannels={shouldRequireTicketChannels}
          shouldShowWorkspaces={shouldShowWorkspaces}
          shouldShowUsers={shouldShowUsers}
          shouldUseMonthPeriod={shouldUseMonthPeriod}
          shouldShowAutomationCode={shouldShowAutomationCode}
          shouldShowAutomationCodeExcept={shouldShowAutomationCodeExcept}
          shouldShowEstablishments={shouldShowEstablishments}
          shouldShowEsignRules={shouldShowEsignRules}
          isLoading={false}
          isGenerateDisabled={!reportCode}
          validationErrors={displayValidationErrors}
          onCreatedStartChange={changeCreatedStartHandler}
          onCreatedEndChange={changeCreatedEndHandler}
          onDateIntervalChange={changeDateIntervalHandler}
          onMonthPeriodTypeChange={changeMonthPeriodTypeHandler}
          onTicketServicesChange={changeTicketServicesHandler}
          onTicketChannelsChange={changeTicketChannelsHandler}
          onWorkspacesChange={changeWorkspacesHandler}
          onUsersChange={changeUsersHandler}
          onAutomationCodeChange={changeAutomationCodeHandler}
          onAutomationCodeExceptChange={changeAutomationCodeExceptHandler}
          onEstablishmentsChange={changeEstablishmentsHandler}
          onEsignRulesChange={changeEsignRulesHandler}
          onGenerate={handleGenerate}
        />
      )}
    </Box>
  );
};

export default ReportExecutionPage;
